import {
  Box,
  Button,
  Card,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BallotContext } from "./ballot.context";
import { QuestionVotePage } from "../question/question-page";
import { ButtonPanel } from "../../../components/button-panel";
import { BallotOptIn } from "./ballot-opt-in";
import { HeavyCard } from "../../../components/heavy-card";
import { Check } from "../../../components/check";
import { ElectionContext } from "../election.context";
import { IProxyVote } from "../../../domain/ballot-api";
import { sortDisplayOrder } from "../util";


export const BallotVote = () => {
  const { election_id } = useParams();

  const { submitStateMap, voter } = useContext(ElectionContext);
  const { ballot, questions, opt_in, valid, prepareForReview, setOptIn, opt_in_details } = useContext(BallotContext);
  const proxy_votes = submitStateMap[ballot.id].proxy_votes;
  const { opt_in_config } = ballot;
  const navigate = useNavigate();
  const _questions = useMemo(() => questions.sort(sortDisplayOrder), [questions])


  const handleSubmit = useCallback(() => {
    prepareForReview();
    navigate(`/voting/${election_id}/ballot/${ballot.id}/review`)
  }, [prepareForReview, navigate, ballot])

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 10)
  }, [])

  const [selectProxiesSaved, setSelectProxiesSaved] = useState<boolean>(false);


  if (proxy_votes && proxy_votes.length > 1 && !selectProxiesSaved) {
    return (
      <ProxyVoterList proxy_votes={proxy_votes} setSelectProxiesSaved={setSelectProxiesSaved} />
    )
  }




  return (
    <>
      {opt_in_config ? <BallotOptIn opt_in_config={opt_in_config} opt_in={opt_in} setOptIn={setOptIn} opt_in_details={opt_in_details} /> : null}
      {opt_in || !ballot.opt_in_config ? (
        <>
          <Grid container>
            {_questions.map((q) => (
              <Grid item xs={12} key={q.id} mb={2}>
                <QuestionVotePage question={q} />
              </Grid>
            ))}

          </Grid>


        </>
      ) : null}
      {
        opt_in === false
          ? <HeavyCard sx={{ p: 3 }}>
            <Typography>You have chosen to opt out of this ballot. You may submit your opt-out feedback.</Typography>
          </HeavyCard>
          : null
      }
      <ButtonPanel
        back={{ to: `/voting/${election_id}` }}
        next={{
          label: "Review",
          disabled: !valid,
          onClick: handleSubmit
        }}
      />
    </>
  );
};

interface ProxyVoterListProps {
  proxy_votes: IProxyVote[];
  setSelectProxiesSaved: (selectProxiesSaved: boolean) => void;
}
export const ProxyVoterList = ({ proxy_votes, setSelectProxiesSaved }: ProxyVoterListProps) => {

  const [proxies, setProxies] = useState<string[]>([]);

  const { setProxyVotes } = useContext(BallotContext);


  const handleProxyClicked = (proxy_vote: IProxyVote) => {
    if (proxy_vote.submitted) {
      return;
    }
    setProxies(p => {
      if (p.includes(proxy_vote.voter_id)) {
        return p.filter(p => p !== proxy_vote.voter_id)
      }
      return [...p, proxy_vote.voter_id]
    })
  }

  const handleSave = useCallback(() => {
    setProxyVotes(proxies);
    setSelectProxiesSaved(true);
  }, [proxies, setProxyVotes, setSelectProxiesSaved])

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">Select Proxies</Typography>
      <Typography>You have 4 proxies assigned to you that have not yet voted.

        <br />
        Please select which proxies you would like to cast a ballot for.
      </Typography>
      <Box>
        <List>
          {proxy_votes.map((proxy_vote, i) => {
            return <ProxyVote
              key={i}
              proxyVote={proxy_vote}
              onClick={handleProxyClicked}
              selected={proxies.includes(proxy_vote.voter_id)}
            />
          })}
        </List>
        <Box p="5" display="flex" justifyContent="flex-end" >
          <Button variant="contained" onClick={handleSave}>
            Next
          </Button>
        </Box>
      </Box>
    </Card>
  )
}

interface ProxyVoteProps {
  selected: boolean;
  proxyVote: IProxyVote;
  onClick: (proxy: IProxyVote) => void;
}
const ProxyVote = ({ proxyVote, onClick, selected }: ProxyVoteProps) => {
  const handleClicked = useCallback(() => {
    if (proxyVote.submitted) {
      return;
    }
    onClick(proxyVote);
  }, [onClick, proxyVote]);
  const subtext = useMemo(() => {
    if (proxyVote.self) {
      if (proxyVote.submitted) {
        return "You (already voted)";
      }
      return "You";
    }
    if (proxyVote.submitted) {
      return "Already voted";
    }
    return;
  }, [proxyVote])
  return (
    <ListItem
      sx={{
        cursor: "pointer",
        padding: 0,
        ":hover": {
          background: "#EEE",
        },
        border: proxyVote.self ? "1px solid #BBB" : "none",
        borderRadius: 2,
      }}
    >
      <ListItemButton
        disabled={proxyVote.submitted}
        onClick={handleClicked}>
        <ListItemIcon>
          <Checkbox
            checked={selected}
            sx={{ padding: "5px" }}
          />
        </ListItemIcon>
        <ListItemText
          primary={proxyVote.label}
          secondary={subtext}
        />
      </ListItemButton>
    </ListItem>
  )
}